import React, { useState, useEffect } from 'react';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { TabView, TabPanel } from 'primereact/tabview';
import { useDialogCrup, ToastService } from '@iamsoftware/react-hooks';
import { useDataTable } from 'src/hooks/useAppDataTable';
import { AgreementConfigService as Service } from '../../service/AgreementConfigService';
import { CommonService } from '../../service/CommonService';

export const DocumentConfig: React.FC = () => {
  const [agreementNoFormatOptions, setAgreementNoFormatOptions] = useState<any[]>([]);
  const tabs = [
    { label: 'Đang sử dụng', statusId: 'AGT_CFG_ACTIVE' },
    { label: 'Ngừng sử dụng', statusId: 'AGT_CFG_INACTIVE' },
    { label: 'Đã xóa', statusId: 'AGT_CFG_DELETED' }
  ];
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const getStatusFilter = () => {
    const currentTab = tabs[activeTabIndex];
    if (currentTab?.statusId === 'AGT_CFG_ACTIVE') {
      return ['AGT_CFG_ACTIVE', 'AGT_CFG_CREATED'];
    }
    return currentTab?.statusId;
  };
  const statusFilter = getStatusFilter();
  const [detailLoading, setDetailLoading] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [, setCurrentAgreementConfigureEnumId] = useState<string | null>(null);
  const [agreementConfigureEnumIdValue, setAgreementConfigureEnumIdValue] = useState<string>('');
  const [agreementConfigureEnumIdError, setAgreementConfigureEnumIdError] = useState<string | null>(null);
  const dataKey = 'agreementPartyTypeConfId';
  const getAgreementConfigureEnumId = () => {
    const formValue = form?.getValue?.() || {};
    return (agreementConfigureEnumIdValue || formValue?.agreementConfigureEnumId || '').trim();
  };

  const validateAgreementConfigureEnumId = (value: string) => {
    if (!value) {
      setAgreementConfigureEnumIdError('Mã loại tài liệu không được bỏ trống.');
      return false;
    }
    setAgreementConfigureEnumIdError(null);
    return true;
  };

  useEffect(() => {
    CommonService.getPseudoIdFormat().then((data: any) => {
      if (data?.listData) {
        setAgreementNoFormatOptions(data.listData.map((item: any) => ({
          value: item.enumId,
          label: item.description
        })));
      }
    }).catch(() => { });
  }, []);

  const handleDelete = async (rowData: any) => {
    try {
      const res = await Service.delete(rowData[dataKey]);
      if (res && res.success === 'Y') {
        ToastService.success('Đã xóa cấu hình');
        reloadLazyData();
      } else {
        ToastService.error(res?.error || 'Không thể xóa cấu hình');
      }
    } catch (e) {
      ToastService.error('Có lỗi khi xóa cấu hình');
    }
  };

  const handleExpire = async (rowData: any) => {
    try {
      const res = await Service.expire(rowData[dataKey]);
      if (res && res.success === 'Y') {
        ToastService.success('Đã ngừng sử dụng cấu hình');
        reloadLazyData();
      } else {
        ToastService.error(res?.error || 'Không thể ngừng sử dụng cấu hình');
      }
    } catch (e) {
      ToastService.error('Có lỗi khi ngừng sử dụng cấu hình');
    }
  };

  const handleActivate = async (rowData: any) => {
    try {
      const res = await Service.activate(rowData[dataKey]);
      if (res && res.success === 'Y') {
        ToastService.success('Đã kích hoạt lại cấu hình');
        reloadLazyData();
      } else {
        ToastService.error(res?.error || 'Không thể kích hoạt cấu hình');
      }
    } catch (e) {
      ToastService.error('Có lỗi khi kích hoạt cấu hình');
    }
  };

  const handleRestore = async (rowData: any) => {
    try {
      const res = await Service.restore(rowData[dataKey]);
      if (res && res.success === 'Y') {
        ToastService.success('Đã khôi phục cấu hình');
        reloadLazyData();
      } else {
        ToastService.error(res?.error || 'Không thể khôi phục cấu hình');
      }
    } catch (e) {
      ToastService.error('Có lỗi khi khôi phục cấu hình');
    }
  };

  const statusBodyTemplate = (rowData: any) => {
    const statusId = rowData.statusId;
    if (statusId === 'AGT_CFG_ACTIVE') {
      return <Tag value="Đang sử dụng" severity="success" />;
    } else if (statusId === 'AGT_CFG_INACTIVE') {
      return <Tag value="Ngừng sử dụng" severity="warning" />;
    } else if (statusId === 'AGT_CFG_CREATED') {
      return <Tag value="Mới tạo" severity="info" />;
    } else if (statusId === 'AGT_CFG_DELETED') {
      return <Tag value="Xóa bỏ" severity="danger" />;
    }
    return <Tag value="Không xác định" severity="secondary" />;
  };

  const { render: renderDialogCrup, create, update, view, form } = useDialogCrup({
    header: 'Loại tài liệu',
    dataKey,
    fields: [
      { field: 'agreementConfigureName', header: 'Tên loại tài liệu', required: true, className: 'md:col-12', disabled: isViewMode },
      {
        field: 'agreementConfigureEnumId',
        header: 'Mã loại tài liệu',
        required: true,
        className: 'md:col-6',
        type: 'custom',
        body: () => {
          const sanitizeAgreementCode = (rawValue: string = ''): string => {
            let value = rawValue.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
            value = value.toUpperCase().replace(/\s/g, '');
            return value;
          };

          const currentValue = agreementConfigureEnumIdValue || '';

          return (
            <div className="field">
              <label htmlFor="agreementConfigureEnumId" className="block text-900 font-medium mb-1">
                Mã loại tài liệu <span className="text-red-500">*</span>
              </label>
              <InputText
                id="agreementConfigureEnumId"
                value={currentValue}
                onInput={(e) => {
                  const inputEl = e.target as HTMLInputElement;
                  const value = sanitizeAgreementCode(inputEl.value);
                  if (value !== inputEl.value) inputEl.value = value;
                }}
                onChange={(e) => {
                  const value = sanitizeAgreementCode(e.target.value);
                  setAgreementConfigureEnumIdValue(value);
                  if (form) {
                    const updatedFormValue = form?.getValue?.() || {};
                    form?.setValue?.({ ...updatedFormValue, agreementConfigureEnumId: value });
                  }
                  if (agreementConfigureEnumIdError) {
                    validateAgreementConfigureEnumId(value);
                  }
                }}
                onBlur={() => validateAgreementConfigureEnumId(getAgreementConfigureEnumId())}
                className={`w-full ${agreementConfigureEnumIdError ? 'p-invalid' : ''}`}
                disabled={isViewMode}
              />
              {agreementConfigureEnumIdError && (
                <small className="p-error block mt-1">{agreementConfigureEnumIdError}</small>
              )}
            </div>
          );
        }
      },
      {
        field: 'agreementNoFormatEnumId',
        header: 'Định dạng mã tài liệu',
        required: true,
        className: 'md:col-6',
        type: 'Dropdown',
        DropdownProps: { options: agreementNoFormatOptions, optionLabel: 'label', optionValue: 'value', placeholder: 'Chọn định dạng', disabled: isViewMode }
      },
      {
        field: 'description',
        header: 'Mô tả',
        className: 'md:col-12',
        type: 'InputTextarea',
        InputTextareaProps: {
          rows: 4,
          maxLength: 500,
          placeholder: 'Nhập mô tả lĩnh vực tài liệu',
          autoResize: true,
          disabled: isViewMode
        }
      },
    ],
    width: '64rem',
    createItem(item: any): Promise<any> {
      const value = getAgreementConfigureEnumId();
      if (!validateAgreementConfigureEnumId(value)) {
        return Promise.reject(new Error('Mã loại tài liệu không được bỏ trống.'));
      }
      const agreementConfigureEnumId = value;
      const itemData = {
        ...item,
        agreementConfigureEnumId: agreementConfigureEnumId
      };
      return Service.create(itemData)
        .then(async res => {
          console.log(res);
          if (res && res.success === 'Y') {
            ToastService.success('Đã tạo cấu hình thành công');
            setCurrentAgreementConfigureEnumId(agreementConfigureEnumId);
            reloadLazyData();
            return res;
          }
          throw new Error(res?.errors || 'Không thể tạo cấu hình');
        })
        .catch((error: any) => {
          let message = 'Có lỗi khi tạo cấu hình';
          if (error?.response?.data?.errors) {
            message = error.response.data.errors;
          } else if (error?.response?.data?.error) {
            message = error.response.data.error;
          } else if (error?.message) {
            message = error.message;
          }

          
          ToastService.error(message);
          throw (error instanceof Error ? error : new Error(message));
        });
    },
    updateItem(id: string, item: any): Promise<any> {
      const formValue = form?.getValue?.() || {};
      // Validate custom field trước khi submit
      const value = getAgreementConfigureEnumId();
      if (!validateAgreementConfigureEnumId(value)) {
        return Promise.reject(new Error('Mã loại tài liệu không được bỏ trống.'));
      }
      const agreementConfigureEnumId = value;
      const agreementPartyTypeConfId = formValue?.agreementPartyTypeConfId || item?.agreementPartyTypeConfId || id;
      const itemData = {
        ...item,
        agreementConfigureEnumId: agreementConfigureEnumId
      };
      return Service.update(agreementPartyTypeConfId, itemData)
        .then(async res => {
          if (res && res.success === 'Y') {
            setCurrentAgreementConfigureEnumId(agreementConfigureEnumId);

            reloadLazyData();
            return res;
          }
          throw new Error(res?.error || 'Không thể cập nhật cấu hình');
        })
        .catch((error: any) => {
          let message = 'Có lỗi khi cập nhật cấu hình';
          if (error?.response?.data?.errors) {
            message = error.response.data.errors;
          } else if (error?.response?.data?.error) {
            message = error.response.data.error;
          } else if (error?.message) {
            message = error.message;
          }

          if (message.includes('value too long')) {
            message = 'Dữ liệu nhập vào quá dài. Vui lòng kiểm tra lại.';
          }

          ToastService.error(message);
          throw (error instanceof Error ? error : new Error(message));
        });
    }
  });

  const handleEdit = async (rowData: any) => {
    setAgreementConfigureEnumIdError(null);
    try {
      setDetailLoading(true);
      const agreementPartyTypeConfId = rowData.agreementPartyTypeConfId;
      if (!agreementPartyTypeConfId) {
        ToastService.error('Không tìm thấy ID cấu hình');
        return;
      }

      const res = await Service.get(agreementPartyTypeConfId);
      if (res && res.success === 'Y' && res.record) {
        const record = res.record;

        setCurrentAgreementConfigureEnumId(record.agreementConfigureEnumId);
        const recordEnumId = record.agreementConfigureEnumId || '';
        setAgreementConfigureEnumIdValue(recordEnumId);
        if (recordEnumId) {
          setAgreementConfigureEnumIdError(null);
        }

        setDetailLoading(false);
        setIsViewMode(false);
        // Sử dụng requestAnimationFrame để đảm bảo DOM đã được update
        requestAnimationFrame(() => {
          update(record);
        });
      } else {
        ToastService.error(res?.error || 'Không thể lấy thông tin cấu hình');
        setDetailLoading(false);
      }
    } catch (e: any) {
      ToastService.error('Có lỗi khi lấy thông tin cấu hình: ' + (e?.message || 'Unknown error'));
      setDetailLoading(false);
    } finally {
      // ensure spinner closes if we exited early before data load
      setDetailLoading(false);
    }
  };

  const handleView = async (rowData: any) => {
    setAgreementConfigureEnumIdError(null);
    try {
      setDetailLoading(true);
      const agreementPartyTypeConfId = rowData.agreementPartyTypeConfId;
      if (!agreementPartyTypeConfId) {
        ToastService.error('Không tìm thấy ID cấu hình');
        return;
      }
      const res = await Service.get(agreementPartyTypeConfId);
      if (res && res.success === 'Y' && res.record) {
        const record = res.record;
        setCurrentAgreementConfigureEnumId(record.agreementConfigureEnumId);
        const recordEnumId = record.agreementConfigureEnumId || '';
        setAgreementConfigureEnumIdValue(recordEnumId);
        if (recordEnumId) {
          setAgreementConfigureEnumIdError(null);
        }
        setDetailLoading(false);
        setIsViewMode(true);
        requestAnimationFrame(() => {
          view(record);
        });
      } else {
        ToastService.error(res?.error || 'Không thể lấy thông tin cấu hình');
        setDetailLoading(false);
      }
    } catch (e: any) {
      ToastService.error('Có lỗi khi lấy thông tin cấu hình: ' + (e?.message || 'Unknown error'));
      setDetailLoading(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCreate = () => {
    setCurrentAgreementConfigureEnumId(null);
    setAgreementConfigureEnumIdValue('');
    setAgreementConfigureEnumIdError(null);
    setIsViewMode(false);
    form?.reset?.();
    // Sử dụng requestAnimationFrame để đảm bảo DOM đã được update
    requestAnimationFrame(() => {
      create();
    });
  };

  const { render: renderDataTable, reloadLazyData } = useDataTable({
    recordLabel: 'loại tài liệu',
    headerActions: (
      <Button
        icon="pi pi-plus"
        label="Thêm loại tài liệu"
        severity="success"
        onClick={handleCreate}
        size="small"
      />
    ),
    dataKey,
    columns: [
      { field: 'agreementConfigureName', header: 'Tên loại tài liệu', matchMode: 'contains', minWidth: 200 },
      { field: 'agreementConfigureEnumId', header: 'Mã loại tài liệu', matchMode: 'contains', minWidth: 150 },
      { field: 'agreementNoFormatEnum', header: 'Loại tài liệu', matchMode: 'contains', minWidth: 150 },
      { field: 'description', header: 'Mô tả', matchMode: 'contains', minWidth: 200 },
      {
        field: 'statusId',
        header: 'Trạng thái',
        minWidth: 130,
        dataType: 'custom',
        customCell(rowData: any): any {
          return statusBodyTemplate(rowData);
        }
      },
    ],
    indexColumnWidth: 56,
    actionColumnWidth: 90,
    getList(lazyLoadEvent: any): Promise<any> {
      let eventData = lazyLoadEvent;
      if (typeof lazyLoadEvent === 'string') {
        try {
          eventData = JSON.parse(lazyLoadEvent);
        } catch (e) {
          eventData = { first: 0, rows: 20, filters: {} };
        }
      }
      if (!eventData.filters) eventData.filters = {};
      if (statusFilter) {
        if (Array.isArray(statusFilter)) {
          eventData.filters.statusId = { value: statusFilter, matchMode: 'in' };
        } else {
          eventData.filters.statusId = { value: statusFilter, matchMode: 'equals' };
        }
      }
      return Service.getList(JSON.stringify(eventData)).then(result => {
        if (result.listData && !result.list) {
          result.list = result.listData;
        }
        return result;
      });
    },
    initActions(items: Array<any>) {
      if (items) {
        items.forEach(item => {
          const statusId = item.statusId;
          const actions: any[] = [

          ];

          // Hiển thị nút theo trạng thái
          if (statusId === 'AGT_CFG_ACTIVE') {
            actions.push({
              icon: 'pi pi-stop',
              title: 'Ngừng sử dụng',
              className: 'p-button-warning',
              command: () => handleExpire(item)
            });
            actions.push({ icon: 'pi pi-eye', title: 'Xem', className: 'p-button-secondary', command: () => handleView(item) });
            actions.push({ icon: 'pi pi-pencil', title: 'Sửa', className: 'p-button-info', command: () => handleEdit(item) });
          } else if (statusId === 'AGT_CFG_INACTIVE') {
            actions.push({
              icon: 'pi pi-play',
              title: 'Sử dụng lại',
              className: 'p-button-success',
              command: () => handleActivate(item)
            });
            actions.push({ icon: 'pi pi-eye', title: 'Xem', className: 'p-button-secondary', command: () => handleView(item) });
            actions.push({ icon: 'pi pi-pencil', title: 'Sửa', className: 'p-button-info', command: () => handleEdit(item) });
          } else if (statusId === 'AGT_CFG_CREATED') {
            actions.push({
              icon: 'pi pi-trash',
              title: 'Xóa',
              className: 'p-button-danger',
              command: () => handleDelete(item)
            });
            actions.push({ icon: 'pi pi-eye', title: 'Xem', className: 'p-button-secondary', command: () => handleView(item) });
            actions.push({ icon: 'pi pi-pencil', title: 'Sửa', className: 'p-button-info', command: () => handleEdit(item) });
          } else if (statusId === 'AGT_CFG_DELETED') {
            actions.push({
              icon: 'pi pi-refresh',
              title: 'Khôi phục',
              className: 'p-button-success',
              command: () => handleRestore(item)
            });
            actions.push({ icon: 'pi pi-eye', title: 'Xem', className: 'p-button-secondary', command: () => handleView(item) });
          }

          item.actions = actions;
        });
      }
    },
    originalProps: { scrollHeight: 'calc(100vh - 300px)' }
  });


  // Ẩn actions trong dialog footer và trong bảng khi ở chế độ view
  useEffect(() => {
    const hideActions = () => {
      const dialog = document.querySelector('.p-dialog');
      if (!dialog) return;

      // Ẩn buttons trong footer
      const footer = dialog.querySelector('.p-dialog-footer');
      if (footer) {
        const actionButtons = footer.querySelectorAll('button');
        actionButtons.forEach((btn: any) => {
          const btnText = btn.textContent?.trim() || '';
          const btnLabel = btn.getAttribute('aria-label') || '';
          // Giữ lại button "Đóng", "Hủy", "X", ẩn các button khác như "Lưu", "Tạo", "Cập nhật"
          if (isViewMode) {
            if (btnText.includes('Đóng') || btnText.includes('Hủy') || btnLabel.includes('close') || btn.classList.contains('p-dialog-header-close')) {
              btn.style.display = '';
            } else if (btnText && !btnText.includes('Đóng') && !btnText.includes('Hủy')) {
              btn.style.display = 'none';
            }
          } else {
            btn.style.display = '';
          }
        });
      }

      // Ẩn buttons xóa trong bảng "Danh sách file đính kèm"
      const deleteButtons = dialog.querySelectorAll('button.p-button-danger, button[class*="p-button-danger"]');
      deleteButtons.forEach((btn: any) => {
        if (isViewMode) {
          btn.style.display = 'none';
        } else {
          btn.style.display = '';
        }
      });

      // Disable các input fields trong bảng khi ở chế độ view
      const tableInputs = dialog.querySelectorAll('table input, table textarea, table .p-checkbox');
      tableInputs.forEach((input: any) => {
        if (isViewMode) {
          if (input.disabled === undefined) {
            input.style.pointerEvents = 'none';
            input.style.opacity = '0.6';
          } else {
            input.disabled = true;
          }
        } else {
          if (input.disabled === undefined) {
            input.style.pointerEvents = '';
            input.style.opacity = '';
          } else if (input.hasAttribute('data-original-disabled')) {
            input.disabled = input.getAttribute('data-original-disabled') === 'true';
          }
        }
      });
    };

    // Chạy ngay lập tức
    hideActions();

    // Chạy lại sau các khoảng thời gian để đảm bảo dialog đã render
    const timers = [100, 300, 500].map(delay => setTimeout(hideActions, delay));

    // Sử dụng MutationObserver để theo dõi thay đổi trong DOM
    const observer = new MutationObserver(() => {
      hideActions();
    });

    const dialog = document.querySelector('.p-dialog');
    if (dialog) {
      observer.observe(dialog, { childList: true, subtree: true, attributes: true });
    }

    return () => {
      timers.forEach(timer => clearTimeout(timer));
      observer.disconnect();
    };
  }, [isViewMode]);

  return (
    <div className="card pt-2 border-none shadow-1 border-round-xl" style={{ backgroundColor: 'var(--surface-ground)' }}>
      <style dangerouslySetInnerHTML={{
        __html: `
        .loai-tai-lieu-table .p-datatable-thead > tr:first-child > th {
          position: sticky !important; top: 0 !important; z-index: 10 !important;
          background: var(--surface-card) !important;
        }
        .loai-tai-lieu-table .p-datatable-thead > tr:nth-child(2) > th {
          position: sticky !important; top: 38px !important; z-index: 9 !important;
          background: var(--surface-card) !important;
        }
      `}} />
      <TabView activeIndex={activeTabIndex} onTabChange={(e) => { setActiveTabIndex(e.index); reloadLazyData(); }}>
        {tabs.map((tab, idx) => (
          <TabPanel key={tab.statusId} header={tab.label}>
            <div className="loai-tai-lieu-table">
              {renderDataTable()}
            </div>
          </TabPanel>
        ))}
      </TabView>
      {renderDialogCrup()}

      <Dialog
        header={null}
        visible={detailLoading}
        modal
        closable={false}
        closeOnEscape={false}
        dismissableMask={false}
        style={{ width: '32rem' }}
        onHide={() => setDetailLoading(false)}
      >
        <div className="flex flex-column align-items-center justify-content-center py-6">
          <i className="pi pi-spin pi-spinner mb-3" style={{ fontSize: '2.8rem', color: 'var(--primary-color)' }} />
          <div className="text-center text-xl font-semibold mb-1" style={{ color: 'var(--primary-color)' }}>
            Đang tải dữ liệu cấu hình...
          </div>
          <small className="text-center" style={{ color: 'var(--text-color-secondary)' }}>Vui lòng đợi trong giây lát</small>
        </div>
      </Dialog>
    </div>
  );
};

const toDateValue = (value: any): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;

  if (typeof value === 'number') {
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }

  if (typeof value === 'string') {
    const normalized = value.includes('T') ? value : value.replace(' ', 'T');
    const date = new Date(normalized);
    if (!isNaN(date.getTime())) return date;
    const asNumber = Number(value);
    if (!Number.isNaN(asNumber)) {
      const dateFromNumber = new Date(asNumber);
      return isNaN(dateFromNumber.getTime()) ? null : dateFromNumber;
    }
    return null;
  }

  if (typeof value === 'object') {
    if (value?.time) {
      const date = new Date(value.time);
      return isNaN(date.getTime()) ? null : date;
    }
    if (value?.date) {
      const date = new Date(value.date);
      return isNaN(date.getTime()) ? null : date;
    }
  }

  return null;
};
export { TemplateDocumentConfig } from './tai-lieu-mau';

